import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { createComicProject, deleteComicProject, isValidComicProjectPublicId, listComicProjects } from '../api/comics'
import { isApiError, rethrowIfUnauthorized } from '../api/errors'
import { AsyncPullLoadingIndicator } from '../components/async/AsyncPullLoadingIndicator'
import { PageAsyncErrorState } from '../components/async/PageAsyncErrorState'
import {
  ChromePrimaryButton,
  ChromeTextInput,
  ChromeToolbarButton,
  ChromeToolbarDangerButton,
} from '../components/ChromeActions'
import { ChromeTooltip } from '../components/ChromeTooltip'
import { ComicBookshelf } from '../components/comic/ComicBookshelf'
import { ComicCreativeSettingsForm } from '../components/comic/ComicCreativeSettingsForm'
import { ThemedComicState } from '../components/comic/ThemedComicState'
import { TimelinePullIndicator } from '../components/create/TimelineRefreshSurfaces'
import { ThemeModal } from '../components/modal'
import { ThemeReveal } from '../components/motion/ThemeMotion'
import { usePlatformConfig } from '../context/PlatformConfigContext'
import { useToast } from '../context/ToastContext'
import { useScrollPullToRefresh } from '../hooks/useScrollPullToRefresh'
import { USER_PERMISSION, useUserPerm } from '../hooks/useUserPerm'
import { ContentSkeleton } from '../layouts/ContentSkeleton'
import { showApiErrorToast } from '../lib/apiErrorToast'
import { buildDefaultComicProjectCreative, validateComicProjectCreative } from '../lib/comicCreativeBible'
import {
  buildDefaultComicProjectSettings,
  syncComicProjectSettingsToPanelAspect,
  validateComicProjectSettingsForPanelAspect,
} from '../lib/comicProjectSettings'
import { COMIC_PROJECT_INIT_TEMPLATE_PUBLIC_ID } from '../lib/workflow/workflowComicProjectInit'
import {
  isPlatformCatalogLoadFailed,
  isPlatformCatalogLoadPending,
} from '../lib/platformConfigLoadState'
import type { ComicProject } from '../lib/comicTypes'
import { useTheme } from '../theme/ThemeContext'



export function ComicProjectsPage() {

  const { theme } = useTheme()

  const angular = theme === 'pixel' || theme === 'geek'

  const navigate = useNavigate()

  const { showToast } = useToast()

  const {
    clientUi,
    remoteSynced: platformCatalogReady,
    loading: platformLoading,
    error: platformError,
    refresh: refreshPlatform,
  } = usePlatformConfig()

  const canCreateComic = useUserPerm(USER_PERMISSION.comicProjectCreate)
  const canDeleteComic = useUserPerm(USER_PERMISSION.comicProjectDelete)

  const [projects, setProjects] = useState<ComicProject[]>([])

  const [projectsLoading, setProjectsLoading] = useState(false)

  const [error, setError] = useState<string | null>(null)

  const [createChoiceOpen, setCreateChoiceOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  const [creating, setCreating] = useState(false)
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ComicProject | null>(null)

  const [newTitle, setNewTitle] = useState('')

  const [newSynopsis, setNewSynopsis] = useState('')

  const [createCreative, setCreateCreative] = useState(() => buildDefaultComicProjectCreative())
  const defaultCreateSettings = useMemo(
    () => (platformCatalogReady ? buildDefaultComicProjectSettings(clientUi) : null),
    [clientUi, platformCatalogReady],
  )

  const comicScrollRef = useRef<HTMLDivElement | null>(null)
  const [comicPullAttachSignal, setComicPullAttachSignal] = useState(0)
  const setComicScrollPort = useCallback((node: HTMLDivElement | null) => {
    comicScrollRef.current = node
    setComicPullAttachSignal((n) => n + 1)
  }, [])



  const refresh = useCallback(async () => {
    if (!platformCatalogReady) return

    setProjectsLoading(true)
    setError(null)
    try {
      setProjects(await listComicProjects(clientUi))
    } catch (e) {
      rethrowIfUnauthorized(e)
      const msg = isApiError(e) ? e.message : e instanceof Error ? e.message : '加载失败'
      setError(msg)
      setProjects([])
    } finally {
      setProjectsLoading(false)
    }
  }, [clientUi, platformCatalogReady])



  useEffect(() => {

    void refresh()

  }, [refresh])



  const openCreateChoice = () => {
    setCreateChoiceOpen(true)
  }

  const openCreate = () => {

    setNewTitle('')

    setNewSynopsis('')

    setCreateCreative(buildDefaultComicProjectCreative())
    setCreateOpen(true)

  }

  const openWorkflowCreate = () => {
    setCreateChoiceOpen(false)
    navigate(`/workflows?mode=run&template=${encodeURIComponent(COMIC_PROJECT_INIT_TEMPLATE_PUBLIC_ID)}`)
  }



  const confirmCreate = async () => {

    const title = newTitle.trim()

    if (!title) {

      showToast({ message: '请填写作品名称', variant: 'warning' })

      return

    }

    if (!platformCatalogReady) {

      showToast({ message: '模型配置未就绪', variant: 'error' })

      return

    }

    const creativeValidation = validateComicProjectCreative(createCreative)
    if (!creativeValidation.ok) {
      showToast({ message: creativeValidation.message, variant: 'warning' })
      return
    }
    const resolvedSettings = syncComicProjectSettingsToPanelAspect(
      defaultCreateSettings ?? buildDefaultComicProjectSettings(clientUi),
      clientUi,
      createCreative.panelAspect,
    )
    const settingsValidation = validateComicProjectSettingsForPanelAspect(
      resolvedSettings,
      clientUi,
      createCreative.panelAspect,
    )
    if (!settingsValidation.ok) {
      showToast({ message: settingsValidation.message, variant: 'warning' })
      return
    }

    setCreating(true)

    try {

      const project = await createComicProject(clientUi, {

        title,

        synopsis: newSynopsis,

        settings: resolvedSettings,
        creative: createCreative,
      })

      setCreateOpen(false)

      showToast({ message: `已创建《${project.title}》`, variant: 'success', autoCloseMs: 3500 })

      if (!isValidComicProjectPublicId(project.id)) {
        showToast({ message: '漫画已创建但缺少标识，请从书架重新打开', variant: 'warning' })
        void refresh()
        return
      }

      navigate(`/comics/${encodeURIComponent(project.id)}`)

    } catch (e) {
      rethrowIfUnauthorized(e)
      showApiErrorToast(showToast, e, '创建失败')
    } finally {

      setCreating(false)

    }

  }

  const requestDeleteProject = useCallback((project: ComicProject) => {
    setDeleteTarget(project)
  }, [])

  const confirmDeleteProject = useCallback(async () => {
    if (!deleteTarget) return
    const project = deleteTarget
    setDeletingProjectId(project.id)
    try {
      await deleteComicProject(project.id)
      setProjects((prev) => prev.filter((p) => p.id !== project.id))
      setDeleteTarget(null)
      showToast({ message: `已删除《${project.title}》`, variant: 'success', autoCloseMs: 2200 })
    } catch (e) {
      rethrowIfUnauthorized(e)
      showApiErrorToast(showToast, e, '删除失败')
    } finally {
      setDeletingProjectId(null)
    }
  }, [deleteTarget, showToast])



  const headerTrailing = canCreateComic ? (
    <ChromeToolbarButton
      type="button"
      className="h-9 lg:hidden"
      onClick={openCreateChoice}
      disabled={!platformCatalogReady && !platformLoading}
    >
      新建漫画
    </ChromeToolbarButton>
  ) : null



  const showEmpty = !projectsLoading && !error && projects.length === 0
  const platformLoadSnapshot = {
    loading: platformLoading,
    remoteSynced: platformCatalogReady,
    error: platformError,
  }
  const platformPending = isPlatformCatalogLoadPending(platformLoadSnapshot)
  const platformFailed = isPlatformCatalogLoadFailed(platformLoadSnapshot)
  const showInitialLoading =
    platformPending || (projectsLoading && projects.length === 0 && !error && platformCatalogReady)

  const shouldIgnoreComicPullStart = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) return false
    return Boolean(
      target.closest('button, input, textarea, select, [contenteditable="true"], [role="button"]'),
    )
  }, [])

  const onComicPullRefreshCommit = useCallback(() => {
    void (async () => {
      if (!platformCatalogReady) {
        await refreshPlatform()
      }
      await refresh()
    })()
  }, [platformCatalogReady, refresh, refreshPlatform])

  const {
    pullPx: comicPullPx,
    commitFlying: comicPullCommitFlying,
    onCommitFlyComplete: onComicPullCommitFlyComplete,
    releaseThreshold: comicPullReleaseThreshold,
  } = useScrollPullToRefresh({
    scrollRef: comicScrollRef,
    attachSignal: comicPullAttachSignal,
    enabled: !showInitialLoading,
    refreshing: projectsLoading && !showInitialLoading,
    shouldIgnorePullStart: shouldIgnoreComicPullStart,
    onCommit: onComicPullRefreshCommit,
  })

  const comicPullActive = comicPullPx > 0 || comicPullCommitFlying



  return (

    <>

      <ContentSkeleton

        title="漫画"

        breadcrumbs={[{ label: '首页', to: '/' }, { label: '漫画' }]}

        headerTrailing={headerTrailing}

        adminLayout

        framed={false}

        scrollableBody={false}

        shellScrollable={false}

      >

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:h-full lg:min-h-0 lg:flex-row">

          <div
            ref={setComicScrollPort}
            className="comic-shelf-scroll scroll-region flex min-h-0 min-w-0 flex-1 flex-col overflow-x-clip overflow-y-auto overscroll-y-contain px-4 pb-6 pt-2 sm:px-6 sm:pb-8 sm:pt-3 lg:min-h-0 lg:px-8 xl:px-10 2xl:px-12"
          >
            <div
              className={[
                'will-change-transform flex min-h-0 min-w-0 flex-1 flex-col',
                comicPullActive ? 'select-none' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ transform: `translate3d(0, ${comicPullPx}px, 0)` }}
            >
              <div className="select-none" aria-live="polite">
                {(projectsLoading && !showInitialLoading) ||
                comicPullPx >= 8 ||
                comicPullCommitFlying ? (
                  <TimelinePullIndicator
                    pullPx={comicPullCommitFlying ? comicPullReleaseThreshold : comicPullPx}
                    releaseThreshold={comicPullReleaseThreshold}
                    angular={angular}
                    commitFly={comicPullCommitFlying}
                    onCommitFlyComplete={onComicPullCommitFlyComplete}
                    refreshing={projectsLoading && !showInitialLoading}
                  />
                ) : (
                  <div className="h-0 min-h-px shrink-0" aria-hidden />
                )}
              </div>

              {showInitialLoading ? (
                <AsyncPullLoadingIndicator
                  angular={angular}
                  label={
                    platformPending
                      ? theme === 'geek'
                        ? 'loading_catalog…'
                        : '正在加载模型配置…'
                      : theme === 'geek'
                        ? 'loading_shelf…'
                        : '正在加载书架…'
                  }
                  className="pb-4"
                />
              ) : platformFailed ? (
                <PageAsyncErrorState
                  theme={theme}
                  title={theme === 'geek' ? 'catalog_fetch_failed' : '模型配置加载失败'}
                  detail={
                    platformError ??
                    '请确认后端已启动且可访问「平台配置」接口，然后重试。'
                  }
                  onRetry={() => void refreshPlatform()}
                />
              ) : error ? (
                <ThemedComicState theme={theme} kind="error" detail={error} onRetry={() => void refresh()} />
              ) : showEmpty ? (
                <ThemedComicState
                  theme={theme}
                  kind="empty"
                  onCreate={canCreateComic ? openCreate : undefined}
                />
              ) : (
                <ThemeReveal>
                  <ComicBookshelf
                    projects={projects}
                    angular={angular}
                    layoutReflow={!comicPullActive}
                    onDeleteProject={
                      canDeleteComic && !deletingProjectId ? requestDeleteProject : undefined
                    }
                  />
                </ThemeReveal>
              )}
            </div>
          </div>

          <aside
            className={[
              'shell-rail hidden h-full min-h-0 w-16 shrink-0 flex-col items-center border-line bg-panel/70 lg:flex',
              angular ? 'border-l-2' : 'border-l',
            ].join(' ')}
          >
            <div className="scroll-region flex min-h-0 flex-1 flex-col items-center gap-3 overflow-y-auto p-2">
              {canCreateComic ? (
                <ChromeTooltip content="新建漫画" angular={angular} side="top" align="center">
                  <span className="inline-flex">
                    <ChromePrimaryButton
                      type="button"
                      density="compact"
                      className="h-10 w-10 !p-0"
                      onClick={openCreateChoice}
                      disabled={!platformCatalogReady && !platformLoading}
                      aria-label="新建漫画"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M12 5v14M5 12h14"/></svg>
                    </ChromePrimaryButton>
                  </span>
                </ChromeTooltip>
              ) : null}
            </div>
          </aside>

        </div>

      </ContentSkeleton>

      <ThemeModal
        open={createChoiceOpen}
        onClose={() => setCreateChoiceOpen(false)}
        title="选择创建方式"
        angular={angular}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setCreateChoiceOpen(false)}>
              取消
            </ChromeToolbarButton>
          </div>
        }
      >
        <div className="space-y-3">
          <button
            type="button"
            className="block w-full rounded-theme border border-line bg-panel px-4 py-3 text-left transition-colors hover:bg-surface-muted"
            onClick={() => {
              setCreateChoiceOpen(false)
              openCreate()
            }}
          >
            <p className="text-sm font-semibold text-ink">普通创建</p>
            <p className="mt-1 text-xs leading-relaxed text-ink-soft">沿用当前创建流程，立即创建空白漫画项目并进入编辑器。</p>
          </button>
          <button
            type="button"
            className="block w-full rounded-theme border border-line bg-panel px-4 py-3 text-left transition-colors hover:bg-surface-muted"
            onClick={openWorkflowCreate}
          >
            <p className="text-sm font-semibold text-ink">工作流创建</p>
            <p className="mt-1 text-xs leading-relaxed text-ink-soft">进入工作流市场系统模板，先生成故事蓝图与章节规划，再确认创建漫画项目。</p>
          </button>
        </div>
      </ThemeModal>



      <ThemeModal

        open={createOpen}

        onClose={() => setCreateOpen(false)}

        title="新建漫画"

        angular={angular}

        size="lg"

        bodyClassName="px-4 py-4 sm:px-5 sm:py-5"

        footer={

          <div className="flex justify-end gap-2">

            <ChromeToolbarButton type="button" onClick={() => setCreateOpen(false)} disabled={creating}>

              取消

            </ChromeToolbarButton>

            <ChromePrimaryButton

              density="compact"

              type="button"

              onClick={() => void confirmCreate()}

              disabled={creating || !platformCatalogReady}

            >

              {creating ? '创建中…' : '创建并编辑'}

            </ChromePrimaryButton>

          </div>

        }

      >

        <label className="block text-sm font-semibold text-ink">

          作品名称

          <ChromeTextInput

            className="mt-1"

            value={newTitle}

            onChange={(e) => setNewTitle(e.target.value)}

            placeholder="例如：星际邮差"

            maxLength={128}

            autoFocus

          />

        </label>

        <label className="mt-4 block text-sm font-semibold text-ink">

          简介（可选）

          <ChromeTextInput

            className="mt-1"

            value={newSynopsis}

            onChange={(e) => setNewSynopsis(e.target.value)}

            placeholder="一句话梗概"

            maxLength={512}

          />

        </label>

        <div className="mt-5 border-t border-line pt-5">
          <ComicCreativeSettingsForm
            angular={angular}
            clientUi={clientUi}
            modelId={defaultCreateSettings?.modelId ?? ''}
            platformId={defaultCreateSettings?.platformId ?? ''}
            value={createCreative}
            onChange={setCreateCreative}
          />
        </div>

      </ThemeModal>

      <ThemeModal
        open={deleteTarget != null}
        onClose={() => {
          if (!deletingProjectId) setDeleteTarget(null)
        }}
        title="删除画册"
        angular={angular}
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton
              type="button"
              onClick={() => setDeleteTarget(null)}
              disabled={!!deletingProjectId}
            >
              取消
            </ChromeToolbarButton>
            <ChromeToolbarDangerButton
              type="button"
              className="px-4 py-2"
              disabled={!!deletingProjectId}
              onClick={() => void confirmDeleteProject()}
            >
              {deletingProjectId ? '删除中…' : '删除'}
            </ChromeToolbarDangerButton>
          </div>
        }
      >
        <p className="text-sm text-ink">
          确定删除「<span className="font-medium">{deleteTarget?.title ?? ''}</span>」？画册内所有分镜与成图将一并移除，此操作不可恢复。
        </p>
      </ThemeModal>

    </>

  )

}


